import { supabase } from "@/integrations/supabase/client";

export type MynewsdeskAction = "list" | "view";

export interface MynewsdeskParams {
  action?: MynewsdeskAction;
  /** pressrelease | news | blog_post | event | image | video | document | contact_person */
  type_of_media?: string;
  id?: string | number;
  limit?: number;
  offset?: number;
  language?: string;
}

/**
 * Calls the server-side Mynewsdesk proxy. The API key lives only in backend
 * environment variables and is never shipped to the browser.
 */
export async function fetchMynewsdesk<T = unknown>(params: MynewsdeskParams = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke("mynewsdesk", { body: params });
  if (error) throw error;
  if (data && typeof data === "object" && "error" in (data as Record<string, unknown>)) {
    throw new Error(String((data as Record<string, unknown>).error));
  }
  return data as T;
}

export interface PressRelease {
  id: string;
  slug: string;
  title: string;
  summary: string;
  body: string;
  boilerplate?: string;
  image?: string | null;
  imageThumb?: string | null;
  publishedAt: string;
  language?: string;
  sourceUrl?: string;
  /** Raw Mynewsdesk publication type: pressrelease | news | blog_post */
  typeOfMedia: string;
}

/** Site category slug -> Mynewsdesk type_of_media */
export const MYNEWSDESK_TYPE_BY_CATEGORY: Record<string, string> = {
  "press-release": "pressrelease",
  news: "news",
  "blog-post": "blog_post",
};

export const MYNEWSDESK_TYPES = ["pressrelease", "news", "blog_post"] as const;

const asArray = <T,>(value: T | T[] | undefined | null): T[] =>
  value == null ? [] : Array.isArray(value) ? value : [value];

const slugFromUrl = (url: string | undefined, id: string) => {
  if (!url) return id;
  const last = url.split("/").filter(Boolean).pop();
  return last || id;
};

export function normalizePressRelease(item: any): PressRelease {
  return {
    id: String(item.id),
    slug: slugFromUrl(item.url, String(item.id)),
    title: item.header || "",
    summary: item.summary || "",
    body: item.body || "",
    boilerplate: item.boilerplate || "",
    image: item.image || item.image_medium || null,
    imageThumb: item.image_medium || item.image_thumbnail_large || item.image || null,
    publishedAt: (item.published_at || item.created_at || "").replace(" ", "T"),
    language: item.language,
    sourceUrl: item.url,
    typeOfMedia: item.type_of_media || "pressrelease",
  };
}

/**
 * Lists publications of a given Mynewsdesk type (pressrelease | news | blog_post),
 * newest first. The publication type in Mynewsdesk decides where it appears on the site.
 */
export async function listMynewsdeskItems(typeOfMedia: string, limit = 50): Promise<PressRelease[]> {
  const data = await fetchMynewsdesk<any>({ action: "list", type_of_media: typeOfMedia, limit });
  return asArray<any>(data?.items?.item)
    .map((item) => normalizePressRelease({ type_of_media: typeOfMedia, ...item }))
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

export async function listPressReleases(limit = 50): Promise<PressRelease[]> {
  return listMynewsdeskItems("pressrelease", limit);
}

/** Finds a publication by slug across all Mynewsdesk publication types. */
export async function getMynewsdeskItemBySlug(slug: string): Promise<PressRelease | null> {
  const idMatch = slug.match(/(\d+)$/);
  for (const type of MYNEWSDESK_TYPES) {
    if (idMatch) {
      try {
        const data = await fetchMynewsdesk<any>({ action: "view", type_of_media: type, id: idMatch[1] });
        const item = data?.item ?? data?.items?.item;
        const found = Array.isArray(item) ? item[0] : item;
        if (found && found.id) return normalizePressRelease({ type_of_media: type, ...found });
      } catch {
        /* try next type */
      }
    }
    try {
      const all = await listMynewsdeskItems(type, 100);
      const match = all.find((p) => p.slug === slug);
      if (match) return match;
    } catch {
      /* try next type */
    }
  }
  return null;
}

export async function getPressReleaseBySlug(slug: string): Promise<PressRelease | null> {
  return getMynewsdeskItemBySlug(slug);
}
